import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';

import Text from '@g4/boneyard/lib/Text';
import VisuallyHidden from '../VisuallyHidden';
import oldTheme from '../Colors';

import errorIconFilled from '../../../images/boneyard/ic-error-filled.svg';

const ERROR_TYPE = 'error';
const INFO_TYPE = 'info';

export const Bubble = styled.div`
  position: absolute;
  z-index: 10;
  ${props =>
    props.type === ERROR_TYPE
      ? css`
          background-color: ${oldTheme.colors.retro.tooltip.errorBackground};
          border-top: 2px solid ${oldTheme.colors.retro.tooltip.errorBorder};
          box-shadow: 0 2px 5px 0 rgba(191, 54, 12, 0.6),
            0 5px 15px 0 rgba(121, 84, 84, 0.4);
        `
      : css`
          background-color: ${oldTheme.colors.retro.tooltip.infoBackground};
          border-top: 2px solid ${oldTheme.colors.retro.tooltip.infoBorder};
          box-shadow: 0 2px 5px 0 rgba(38, 49, 56, 0.4),
            0 5px 15px 0 rgba(54, 70, 79, 0.4);
        `} bottom: calc(100% + 5px);
  left: auto;
  width: calc(100% * 2 - 40px);
  padding: 30px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    width: 20px;

    ${props => {
      if (props.position) {
        switch (props.position) {
          case POSITION_LEFT:
            return css`
              left: ${props => props.theme.spacing(2)};
            `;

          case POSITION_RIGHT:
            return css`
              left: calc(50%-40px);
              right: ${props => props.theme.spacing(2)};
            `;

          case POSITION_CENTER:
            return css`
              left: 50%;
              transform: translate3d(-50%, 0, 0);
            `;

          default:
            return css`
              left: ${props => props.position}px;
            `;
        }
      }
    }} border-style: solid;
    border-width: ${props => props.theme.spacing(2)};
    ${props =>
      props.type === ERROR_TYPE
        ? css`
            border-color: ${oldTheme.colors.retro.tooltip.errorBackground}
              transparent transparent transparent;
          `
        : css`
            border-color: ${oldTheme.colors.retro.tooltip.infoBackground}
              transparent transparent transparent;
          `};
  }
`;

const TextIcon = styled.img`
  position: absolute;
  top: ${props => props.theme.spacing(2)};
  left: ${props => props.theme.spacing(2)};
  margin: auto;
`;

const POSITION_LEFT = 'left';
const POSITION_RIGHT = 'right';
const POSITION_CENTER = 'center';

export class Tooltip extends Component {
  static propTypes = {
    /** For creating an association between the :input and tooltip */
    id: PropTypes.string,
    /** Tooltip message */
    message: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.element,
    ]),
    /**
     * Arrow alignment
     *
     * When specified as a number, the offset will be applied in pixels from the left side.
     */
    position: PropTypes.oneOfType([
      PropTypes.oneOf([POSITION_LEFT, POSITION_RIGHT, POSITION_CENTER]),
      PropTypes.number,
    ]),
    /** Control the type of tooltip to be rendered */
    type: PropTypes.oneOf([ERROR_TYPE, INFO_TYPE]),
    className: PropTypes.string,
    children: PropTypes.node,
  };

  static defaultProps = { position: POSITION_CENTER, type: ERROR_TYPE };

  constructor(props) {
    super(props);

    this.showTooltip = this.toggleTooltip.bind(this, true);
    this.hideTooltip = this.toggleTooltip.bind(this, false);

    this.id = props.id || _.uniqueId('Tooltip-');

    this.state = { showTooltip: false };
  }

  toggleTooltip(showTooltip) {
    this.setState({ showTooltip });
  }

  render() {
    const messages = _.without(_.castArray(this.props.message), undefined);

    const tooltipFragment = (
      <Bubble
        role="tooltip"
        id={'tooltip'}
        position={this.props.position}
        type={this.props.type}
      >
        {this.props.type === ERROR_TYPE && (
          <TextIcon src={errorIconFilled} alt="Error icon" />
        )}
        {React.isValidElement(this.props.message)
          ? this.props.message
          : messages.map((message, i) => (
              <Text key={i} size={12}>
                {message}
              </Text>
            ))}
      </Bubble>
    );

    return (
      <div
        className={this.props.className}
        onMouseOver={this.showTooltip}
        onMouseLeave={this.hideTooltip}
        onFocus={this.showTooltip}
        onBlur={this.hideTooltip}
      >
        {messages.length && this.state.showTooltip ? (
          tooltipFragment
        ) : (
          <VisuallyHidden children={tooltipFragment} />
        )}

        {this.props.children}
      </div>
    );
  }
}

export default styled(Tooltip)`
  position: relative;
`;
